/**
 * Matching P-S — Contract Overview & Match-Instruct API slice.
 *
 * Endpoint backend:
 *   GET /matching-ps/s-contract-rows  ← frmMatchingPSMatchLocalSQry / v_report_matching_locals
 *   GET /matching-ps/p-contract-rows  ← frmMatchingPSMatchLocalPQry / v_report_matching_localp
 *   GET /matching-ps/match-p-overview ← frmMatchingPSQryContractPOverview / v_match_p_overview
 *   GET /matching-ps/match-s-overview ← frmMatchingPSQryContractSOverview / v_match_s_overview
 *   GET /matching-ps/match-categories ← SELECT DISTINCT category FROM list_and_valo
 */
import { apiSlice } from './apiSlice'

export interface MatchingPsListResponse {
  items: Record<string, unknown>[]
  total: number
  page: number
  pageSize: number
}

export interface MatchingPsListQuery {
  page: number
  pageSize: number
  q?: string
}

export interface MatchOverviewQuery {
  startDate?: string
  endDate?: string
  category?: string
  page?: number
  pageSize?: number
}

const matchingPsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMatchingPsSContract: builder.query<MatchingPsListResponse, MatchingPsListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/matching-ps/s-contract-rows',
        params: { page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['MatchingPs'],
    }),

    getMatchingPsPContract: builder.query<MatchingPsListResponse, MatchingPsListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/matching-ps/p-contract-rows',
        params: { page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['MatchingPs'],
    }),

    /** Match - Instruct subFormP — Purchase. */
    getMatchPOverview: builder.query<MatchingPsListResponse, MatchOverviewQuery>({
      query: ({ startDate, endDate, category, page = 1, pageSize = 500 }) => ({
        url: '/matching-ps/match-p-overview',
        params: {
          page,
          pageSize,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(category ? { category } : {}),
        },
      }),
      providesTags: ['MatchingPs'],
    }),

    /** Match - Instruct subFormS — Sale. */
    getMatchSOverview: builder.query<MatchingPsListResponse, MatchOverviewQuery>({
      query: ({ startDate, endDate, category, page = 1, pageSize = 500 }) => ({
        url: '/matching-ps/match-s-overview',
        params: {
          page,
          pageSize,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(category ? { category } : {}),
        },
      }),
      providesTags: ['MatchingPs'],
    }),

    /** Grade dropdown options. */
    getMatchCategories: builder.query<string[], void>({
      query: () => '/matching-ps/match-categories',
      providesTags: ['MatchingPs'],
    }),

    /**
     * Edit Sale Plan — RowSource combo `ins_side_cb`
     * (frmMatchingPSMainSubFormEditPlan_s).
     */
    getEditPlanSPurchaseCandidates: builder.query<EditPlanPurchaseCandidate[], void>({
      query: () => '/matching-ps/edit-plan-s/purchase-candidates',
      providesTags: ['MatchingPs'],
    }),

    /** Edit Sale Plan — soft-delete + insert new (VBA `btnSavePlan_Click`). */
    saveEditPlanS: builder.mutation<EditPlanSSaveResult, EditPlanSSavePayload>({
      query: (body) => ({
        url: '/matching-ps/edit-plan-s/save',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /**
     * Edit Purchase Plan — RowSource combo `ins_side_cb`
     * (frmMatchingPSMainSubFormEditPlan_p).
     */
    getEditPlanPSaleCandidates: builder.query<EditPlanSaleCandidate[], void>({
      query: () => '/matching-ps/edit-plan-p/sale-candidates',
      providesTags: ['MatchingPs'],
    }),

    /** Edit Purchase Plan — soft-delete + insert new (VBA `btnSavePlan_Click`). */
    saveEditPlanP: builder.mutation<EditPlanPSaveResult, EditPlanPSavePayload>({
      query: (body) => ({
        url: '/matching-ps/edit-plan-p/save',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /**
     * Direct P-S — subFormD detail grid (24 cột join overview).
     * Backend trỏ thẳng `public.v_match_report_direct`; filter date+grade
     * (mirror Access query_MatchingPSQryReportDirect_all) bắt buộc truyền
     * để bound row scan — Access ODBC paginate nên fast, webapp fetch all.
     */
    getDirectPsDetails: builder.query<Record<string, unknown>[], DirectPsDetailsQuery | void>({
      query: (args) => {
        const a = (args ?? {}) as DirectPsDetailsQuery
        return {
          url: '/matching-ps/direct-ps/details',
          params: {
            ...(a.startDate ? { startDate: a.startDate } : {}),
            ...(a.endDate ? { endDate: a.endDate } : {}),
            ...(a.category ? { category: a.category } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    /** Direct P-S — Save plan (1P↔nS / nP↔1S logic, validation server-side). */
    saveDirectPsPlan: builder.mutation<DirectPsSaveResult, DirectPsSavePayload>({
      query: (body) => ({
        url: '/matching-ps/direct-ps/save',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /** Direct P-S — soft delete (is_status=1) by match_id. */
    deleteDirectPsPlan: builder.mutation<DirectPsDeleteResult, { matchId: number }>({
      query: ({ matchId }) => ({
        url: `/matching-ps/direct-ps/${matchId}/delete`,
        method: 'PATCH',
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /**
     * Delete Contract Plan — frmMatchingPSMainSubFormDelete.
     * Bulk soft-delete tất cả plan rows active của 1 contract theo 3 checkbox tick.
     */
    deleteMatchPlan: builder.mutation<DeleteMatchPlanResult, DeleteMatchPlanPayload>({
      query: (body) => ({
        url: '/matching-ps/delete-plan',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /**
     * Data Plan — frmMatchingPSMainSubFormDataPlan grid.
     * 4 filter modes (mirror cbFilter Value List) route đến 3 view khác nhau.
     */
    getDataPlanRows: builder.query<Record<string, unknown>[], DataPlanQuery>({
      query: ({ filter, startDate, endDate, category }) => ({
        url: '/matching-ps/data-plan',
        params: {
          filter,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(category ? { category } : {}),
        },
      }),
      providesTags: ['MatchingPs'],
    }),

    /* ───────── P - Instruction (frmMatchingPSMainSubFormStockP) ───────── */

    getStockPPurchaseCandidates: builder.query<
      Record<string, unknown>[],
      StockPCandidatesQuery | void
    >({
      query: (args) => {
        const a = (args ?? {}) as StockPCandidatesQuery
        return {
          url: '/matching-ps/stock-p/purchase-candidates',
          params: {
            ...(a.startDate ? { startDate: a.startDate } : {}),
            ...(a.endDate ? { endDate: a.endDate } : {}),
            ...(a.category ? { category: a.category } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    getStockPFactoryOptions: builder.query<StockPFactoryOption[], void>({
      query: () => '/matching-ps/stock-p/factory-options',
      providesTags: ['MatchingPs'],
    }),

    getStockPFactoryStock: builder.query<
      Record<string, unknown>[],
      StockPFactoryStockQuery | void
    >({
      query: (args) => {
        const a = (args ?? {}) as StockPFactoryStockQuery
        return {
          url: '/matching-ps/stock-p/factory-stock',
          params: {
            ...(a.factoryCode ? { factoryCode: a.factoryCode } : {}),
            ...(a.category ? { category: a.category } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    getStockPPlans: builder.query<Record<string, unknown>[], StockPPlansQuery | void>({
      query: (args) => {
        const a = (args ?? {}) as StockPPlansQuery
        return {
          url: '/matching-ps/stock-p/plans',
          params: {
            ...(a.factoryCode ? { factoryCode: a.factoryCode } : {}),
            ...(a.category ? { category: a.category } : {}),
            ...(a.startDate ? { startDate: a.startDate } : {}),
            ...(a.endDate ? { endDate: a.endDate } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    saveStockPPlan: builder.mutation<StockPSaveResult, StockPSavePayload>({
      query: (body) => ({
        url: '/matching-ps/stock-p/plans',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    deleteStockPPlan: builder.mutation<StockPDeleteResult, { matchId: number }>({
      query: ({ matchId }) => ({
        url: `/matching-ps/stock-p/plans/${matchId}/delete`,
        method: 'PATCH',
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /* ───────── S - Instruction (frmMatchingPSMainSubFormStockS) ─────────
       Factory options + factory stock grid dùng chung query với P
       (getStockPFactoryOptions / getStockPFactoryStock — cùng view backend). */

    getStockSSaleCandidates: builder.query<
      Record<string, unknown>[],
      StockSCandidatesQuery | void
    >({
      query: (args) => {
        const a = (args ?? {}) as StockSCandidatesQuery
        return {
          url: '/matching-ps/stock-s/sale-candidates',
          params: {
            ...(a.startDate ? { startDate: a.startDate } : {}),
            ...(a.endDate ? { endDate: a.endDate } : {}),
            ...(a.category ? { category: a.category } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    getStockSPlans: builder.query<Record<string, unknown>[], StockSPlansQuery | void>({
      query: (args) => {
        const a = (args ?? {}) as StockSPlansQuery
        return {
          url: '/matching-ps/stock-s/plans',
          params: {
            ...(a.factoryCode ? { factoryCode: a.factoryCode } : {}),
            ...(a.category ? { category: a.category } : {}),
            ...(a.startDate ? { startDate: a.startDate } : {}),
            ...(a.endDate ? { endDate: a.endDate } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    saveStockSPlan: builder.mutation<StockSSaveResult, StockSSavePayload>({
      query: (body) => ({
        url: '/matching-ps/stock-s/plans',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    deleteStockSPlan: builder.mutation<StockSDeleteResult, { matchId: number }>({
      query: ({ matchId }) => ({
        url: `/matching-ps/stock-s/plans/${matchId}/delete`,
        method: 'PATCH',
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /* ───────── Stock-Sales (frmMatchingPSMatch_Stock_Sales) ───────── */

    /** Datasheet subForm — 22 cột v_match_s_stock. */
    getStockSalesRows: builder.query<
      Record<string, unknown>[],
      StockSalesRowsQuery | void
    >({
      query: (args) => {
        const a = (args ?? {}) as StockSalesRowsQuery
        return {
          url: '/matching-ps/stock-sales/list',
          params: {
            ...(a.startDate ? { startDate: a.startDate } : {}),
            ...(a.endDate ? { endDate: a.endDate } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    /** RowSource combo s_side_cb — Sales contract options. */
    getStockSalesContractOptions: builder.query<StockSalesContractOption[], void>({
      query: () => '/matching-ps/stock-sales/contract-sales-options',
      providesTags: ['MatchingPs'],
    }),

    /** RowSource combo stock_stack — filter warehouse + quality. */
    getStockSalesStackOptions: builder.query<
      StockSalesStackOption[],
      StockSalesStackOptionsQuery | void
    >({
      query: (args) => {
        const a = (args ?? {}) as StockSalesStackOptionsQuery
        return {
          url: '/matching-ps/stock-sales/stack-options',
          params: {
            ...(a.warehouse ? { warehouse: a.warehouse } : {}),
            ...(a.quality ? { quality: a.quality } : {}),
          },
        }
      },
      providesTags: ['MatchingPs'],
    }),

    /** DLookup mv_local_s — Sales contract metadata. */
    getStockSalesSalesMeta: builder.query<StockSalesSalesMeta, { sSide: string }>({
      query: ({ sSide }) => ({
        url: '/matching-ps/stock-sales/sales-meta',
        params: { sSide },
      }),
      providesTags: ['MatchingPs'],
    }),

    /** DSum + DLookup v_match_s_stock — total_matched, s_allocated. */
    getStockSalesAggregates: builder.query<
      StockSalesAggregates,
      { sSide: string; warehouse?: string }
    >({
      query: ({ sSide, warehouse }) => ({
        url: '/matching-ps/stock-sales/sales-aggregates',
        params: { sSide, ...(warehouse ? { warehouse } : {}) },
      }),
      providesTags: ['MatchingPs'],
    }),

    /** DLookup v_matching_inventory — stack info. */
    getStockSalesStackInfo: builder.query<StockSalesStackInfo, { stackId: string }>({
      query: ({ stackId }) => ({
        url: '/matching-ps/stock-sales/stack-info',
        params: { stackId },
      }),
      providesTags: ['MatchingPs'],
    }),

    /** btnSave — INSERT/UPDATE match_s_stack. */
    saveStockSalesMatch: builder.mutation<StockSalesSaveResult, StockSalesSavePayload>({
      query: (body) => ({
        url: '/matching-ps/stock-sales/save',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MatchingPs'],
    }),

    /** btnDelete — soft-delete match_s_stack. */
    deleteStockSalesMatch: builder.mutation<StockSalesDeleteResult, { matchId: number }>({
      query: ({ matchId }) => ({
        url: `/matching-ps/stock-sales/${matchId}/delete`,
        method: 'PATCH',
      }),
      invalidatesTags: ['MatchingPs'],
    }),
  }),
})

/* ───────── P - Instruction types ───────── */

export interface StockPCandidatesQuery {
  startDate?: string
  endDate?: string
  category?: string
}

export interface StockPFactoryOption {
  factory_code: string
  receive_warehouse: string | null
}

export interface StockPFactoryStockQuery {
  factoryCode?: string
  category?: string
}

export interface StockPPlansQuery {
  factoryCode?: string
  category?: string
  startDate?: string
  endDate?: string
}

export interface StockPItemPayload {
  pSide: string
  planPending: number
  matchKg: number | null
}

export interface StockPSavePayload {
  matchDate: string
  note?: string
  factoryName: string
  items: StockPItemPayload[]
}

export interface StockPSaveResult {
  status: 'success'
  message: string
  createdRows: Array<{ p_side: string; match_kg: number }>
}

export interface StockPDeleteResult {
  status: 'success'
  message: string
  matchId: number
}

/* ───────── S - Instruction types ───────── */

export interface StockSCandidatesQuery {
  startDate?: string
  endDate?: string
  category?: string
}

export interface StockSPlansQuery {
  factoryCode?: string
  category?: string
  startDate?: string
  endDate?: string
}

export interface StockSItemPayload {
  sSide: string
  planPending: number
  matchKg: number | null
}

export interface StockSSavePayload {
  matchDate: string
  note?: string
  factoryName: string
  items: StockSItemPayload[]
}

export interface StockSSaveResult {
  status: 'success'
  message: string
  createdRows: Array<{ s_side: string; match_kg: number }>
}

export interface StockSDeleteResult {
  status: 'success'
  message: string
  matchId: number
}

export interface EditPlanPurchaseCandidate {
  contract_no: string
  p_plan_pending: number | null
  quality_code: string | null
  category: string | null
  latest_delivery: string | null
}

export interface EditPlanSSavePayload {
  contract_no: string
  match_date: string // YYYY-MM-DD
  note?: string
  ins_instruct_baoloc: number
  ins_instruct_nhontrach: number
  ins_direct: number
  /** Required khi ins_direct > 0 — contract_no của Purchase được pick. */
  ins_side?: string
  quantity_kg: number
}

export interface EditPlanSSaveResult {
  status: 'ok'
  contract_no: string
  inserted_kg: { direct: number; baoloc: number; nhontrach: number }
}

export interface EditPlanSaleCandidate {
  contract_no: string
  s_plan_pending: number | null
  quality_code: string | null
  category: string | null
  delivery_to: string | null
}

export interface EditPlanPSavePayload {
  contract_no: string
  match_date: string // YYYY-MM-DD
  note?: string
  ins_instruct_baoloc: number
  ins_instruct_nhontrach: number
  ins_direct: number
  /** Required khi ins_direct > 0 — contract_no của Sale được pick. */
  ins_side?: string
  quantity_kg: number
}

export interface EditPlanPSaveResult {
  status: 'ok'
  contract_no: string
  inserted_kg: { direct: number; baoloc: number; nhontrach: number }
}

/** Direct P-S — params filter detail grid (mirror Access _all). */
export interface DirectPsDetailsQuery {
  startDate?: string
  endDate?: string
  category?: string
}

/** Direct P-S — payload save plan. matchKg=null → BE fallback planPending. */
export interface DirectPsRowPayload {
  contractNo: string
  planPending: number
  matchKg: number | null
}

export interface DirectPsSavePayload {
  matchDate: string // YYYY-MM-DD
  note?: string
  purchaseRows: DirectPsRowPayload[]
  saleRows: DirectPsRowPayload[]
}

export interface DirectPsSaveResult {
  status: 'success'
  message: string
  createdRows: Array<{ p_side: string; s_side: string; match_kg: number }>
}

export interface DirectPsDeleteResult {
  status: 'success'
  message: string
  matchId: number
}

/**
 * Delete Contract Plan — payload & response (frmMatchingPSMainSubFormDelete).
 * `side` chỉ định bên (P / S) — BE chọn cột `p_side` hay `s_side` + stock table
 * tương ứng. `affected` báo số rows soft-deleted từng nhóm để FE hiển thị.
 */
export interface DeleteMatchPlanPayload {
  contract_no: string
  side: 'P' | 'S'
  delete_direct: boolean
  delete_baoloc: boolean
  delete_nhontrach: boolean
}

export interface DeleteMatchPlanResult {
  status: 'ok'
  contract_no: string
  side: 'P' | 'S'
  affected: { direct: number; baoloc: number; nhontrach: number }
}

/**
 * Data Plan — params filter cho grid.
 * `filter` chọn 1 trong 4 cbFilter value (mirror Access form_frmMatchingPSMainSubFormDataPlan).
 *   match_date_p      → v_match_report_direct, lọc match_date + p_grade
 *   match_date_s      → v_match_report_direct, lọc match_date + s_grade
 *   latest_delivery_p → v_match_report_stockp, lọc p_time + p_grade
 *   shipment_from_s   → v_match_report_stocks, lọc s_time + s_grade
 */
export type DataPlanFilter =
  | 'match_date_p'
  | 'match_date_s'
  | 'latest_delivery_p'
  | 'shipment_from_s'

export interface DataPlanQuery {
  filter: DataPlanFilter
  startDate?: string
  endDate?: string
  category?: string
}

/* ───────── Stock-Sales types ───────── */

export interface StockSalesRowsQuery {
  startDate?: string
  endDate?: string
}

export interface StockSalesContractOption {
  s_side: string
  stock_side: string
  quality: string | null
  client_short: string | null
  merge_value: string
}

export interface StockSalesStackOptionsQuery {
  warehouse?: string
  quality?: string
}

export interface StockSalesStackOption {
  stack_id: string
  warehouse_short: string | null
  warehouse: string | null
  quality_code: string | null
  date_in: string | null
  stack_can_allocated: number | null
  stack_allocated: number | null
  stack_unallocated: number | null
}

export interface StockSalesSalesMeta {
  client_short?: string | null
  quality?: string | null
  quantity?: number | null
  delivery_to?: string | null
}

export interface StockSalesAggregates {
  total_matched: number
  s_allocated: number
}

export interface StockSalesStackInfo {
  stack_can_allocated?: number | null
  quality_code?: string | null
  warehouse?: string | null
  warehouse_short?: string | null
  date_in?: string | null
  stack_allocated?: number | null
  stack_unallocated?: number | null
}

export interface StockSalesSavePayload {
  matchId?: number | null
  matchDate: string // YYYY-MM-DD
  sSide: string
  stackSide: string
  matchKg: number
  note?: string
}

export interface StockSalesSaveResult {
  status: 'success'
  message: string
  matchId: number | null
}

export interface StockSalesDeleteResult {
  status: 'success'
  message: string
  matchId: number
}

export const {
  useGetMatchingPsSContractQuery,
  useGetMatchingPsPContractQuery,
  useGetMatchPOverviewQuery,
  useGetMatchSOverviewQuery,
  useGetMatchCategoriesQuery,
  useGetEditPlanSPurchaseCandidatesQuery,
  useSaveEditPlanSMutation,
  useGetEditPlanPSaleCandidatesQuery,
  useSaveEditPlanPMutation,
  useGetDirectPsDetailsQuery,
  useSaveDirectPsPlanMutation,
  useDeleteDirectPsPlanMutation,
  useDeleteMatchPlanMutation,
  useGetDataPlanRowsQuery,
  useGetStockPPurchaseCandidatesQuery,
  useGetStockPFactoryOptionsQuery,
  useGetStockPFactoryStockQuery,
  useGetStockPPlansQuery,
  useSaveStockPPlanMutation,
  useDeleteStockPPlanMutation,
  useGetStockSSaleCandidatesQuery,
  useGetStockSPlansQuery,
  useSaveStockSPlanMutation,
  useDeleteStockSPlanMutation,
  useGetStockSalesRowsQuery,
  useGetStockSalesContractOptionsQuery,
  useGetStockSalesStackOptionsQuery,
  useLazyGetStockSalesStackOptionsQuery,
  useGetStockSalesSalesMetaQuery,
  useLazyGetStockSalesSalesMetaQuery,
  useGetStockSalesAggregatesQuery,
  useLazyGetStockSalesAggregatesQuery,
  useGetStockSalesStackInfoQuery,
  useLazyGetStockSalesStackInfoQuery,
  useSaveStockSalesMatchMutation,
  useDeleteStockSalesMatchMutation,
} = matchingPsApi
